/**
 * cellPatch.ts
 * Pure helper for optimistic cell-level updates in the Skill Matrix page.
 *
 * Kept as a separate module so SkillMatrixPage.tsx stays under the 600-line
 * file-size cap while the logic remains easily unit-testable.
 */

import type { SkillMatrixCell } from '../../../../services/scheduleSkillMatrix';

/**
 * Return a new cells array with the Level updated for the given user+skill.
 *
 * Behaviour:
 *  - If a row exists: replace its Level with `newLevel`.
 *  - If `newLevel` is null and a row exists: remove the row (soft-delete
 *    equivalent — the grid renders an empty chip for missing rows).
 *  - If no row exists and `newLevel` is non-null: append a synthetic row
 *    built from `fallbackCell`. The fallback is the cell object assembled by
 *    SkillMatrixGrid (which synthesises all display fields even for empty cells).
 *  - If no row exists and `newLevel` is null: no-op (clearing an already-empty
 *    cell is idempotent).
 *
 * The returned array is always a new reference — safe to pass to React setState.
 */
export function patchCellLevel(
  cells: SkillMatrixCell[],
  userKey: string,
  skillKey: string,
  newLevel: number | null,
  fallbackCell?: SkillMatrixCell,
): SkillMatrixCell[] {
  const idx = cells.findIndex(
    (c) => c.UserKey === userKey && c.SkillKey === skillKey,
  );

  if (idx === -1) {
    // Row not present in local state
    if (newLevel === null) {
      // Clearing an already-empty cell — no-op
      return cells;
    }
    if (!fallbackCell) {
      // No display data available; skip optimistic append — next refetch will
      // reconcile. This should not happen in practice because SkillMatrixGrid
      // always synthesises a full cell before calling onCellClick.
      return cells;
    }
    // Append synthetic row so the grid renders the new level immediately
    return [...cells, { ...fallbackCell, Level: newLevel }];
  }

  if (newLevel === null) {
    // Clear: remove the row so the grid reverts to an empty chip
    const next = [...cells];
    next.splice(idx, 1);
    return next;
  }

  // Update the level in-place
  const next = [...cells];
  next[idx] = { ...cells[idx], Level: newLevel };
  return next;
}
